import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useFinancial } from '../../../context/FinancialContext';
import { fmt, isDiscretionary, sumMonthly, getEmergencyBase } from './walkthroughUtils';

const { FiAlertTriangle, FiShield, FiCheck, FiHome, FiActivity, FiTool, FiUmbrella } = FiIcons;
const EMERGENCIES = [
  { icon: FiTool, title: 'Home repair', detail: 'A broken heater or urgent repair.' },
  { icon: FiActivity, title: 'Medical bill', detail: 'An unexpected deductible.' },
  { icon: FiHome, title: 'Income gap', detail: 'Time to find your next job.' }
];

export const MiniEfTool = () => (
  <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 flex gap-3 items-start">
    <SafeIcon icon={FiAlertTriangle} className="text-indigo-500 mt-0.5 flex-shrink-0 text-base" />
    <div>
      <h4 className="text-[10px] font-black text-indigo-900 uppercase tracking-wider mb-1">Keep it Liquid</h4>
      <p className="text-[11px] font-bold text-indigo-800 leading-relaxed">Keep this $1,000 in an accessible savings account, not in the market. It is there for genuine surprises, not planned purchases.</p>
    </div>
  </div>
);

export const MiniEfVisual = () => (
  <div className="space-y-4">
    <div className="bg-white rounded-3xl border border-indigo-100 p-4 sm:p-5 shadow-sm">
      <p className="text-[9px] font-black uppercase tracking-widest text-indigo-500">When life interrupts the plan</p>
      <div className="emergency-examples grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
        {EMERGENCIES.map(item => (
          <div key={item.title} className="rounded-2xl bg-indigo-50 p-3 border border-indigo-100 flex sm:block items-center gap-3">
            <div className="w-10 h-10 flex-shrink-0 bg-white rounded-xl text-indigo-600 flex items-center justify-center sm:mb-3 shadow-sm">
              <SafeIcon icon={item.icon} className="text-lg" />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-900">{item.title}</p>
              <p className="text-[9px] font-medium text-slate-500 mt-1 leading-snug">{item.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="p-5 bg-indigo-600 rounded-2xl text-white flex items-center gap-4">
      <SafeIcon icon={FiUmbrella} className="text-3xl flex-shrink-0" />
      <div>
        <p className="text-2xl font-black">$1,000</p>
        <p className="text-[10px] font-bold text-indigo-100">A starter buffer so a surprise does not become credit-card debt.</p>
      </div>
    </div>
    <p className="flex items-center gap-2 text-[10px] font-bold text-slate-500">
      <SafeIcon icon={FiCheck} className="text-emerald-500 flex-shrink-0" />
      Separate, accessible, and reserved for emergencies.
    </p>
  </div>
);

export const FullEfTool = ({ shared, patch }) => {
  const { state } = useFinancial();
  const required = sumMonthly(state.expenses.filter(item => !isDiscretionary(item)));
  const base = getEmergencyBase(shared, required);
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest ml-1">Monthly essential expenses</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-black text-xs">$</span>
          <input type="number" min="0" value={base} onChange={event => patch({ emergencyExpenses: parseFloat(event.target.value) || 0 })} className="w-full pl-7 pr-4 py-3 bg-slate-50 rounded-xl font-black text-sm text-slate-900 border-none outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <p className="text-[9px] font-bold text-slate-400 ml-1">{shared.emergencyExpenses == null && required > 0 ? 'Using required expenses from your Budget by default.' : 'Your emergency-fund planning amount; Budget entries are unchanged.'}</p>
        {required > 0 && shared.emergencyExpenses != null && (
          <button onClick={() => patch({ emergencyExpenses: null })} className="text-[9px] font-black uppercase tracking-widest text-indigo-600 ml-1">Reset to required expenses ({fmt(required)}/mo)</button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
          <p className="text-[8px] font-black text-slate-400 uppercase mb-1">3 Month Floor</p>
          <p className="text-sm font-black text-slate-900">{fmt(base * 3)}</p>
        </div>
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
          <p className="text-[8px] font-black text-slate-400 uppercase mb-1">6 Month Target</p>
          <p className="text-sm font-black text-slate-900">{fmt(base * 6)}</p>
        </div>
      </div>
      <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100 flex gap-2 items-start">
        <SafeIcon icon={FiShield} className="text-indigo-500 mt-0.5 flex-shrink-0" />
        <p className="text-[10px] font-bold text-indigo-800">Variable income or an unstable industry? Consider 6–12 months of essential expenses instead.</p>
      </div>
    </div>
  );
};

export const FullEfVisual = ({ shared }) => {
  const { state } = useFinancial();
  const required = sumMonthly(state.expenses.filter(item => !isDiscretionary(item)));
  const base = getEmergencyBase(shared, required);
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-3xl p-4 sm:p-5 border border-indigo-100 shadow-sm">
        <p className="text-[9px] font-black uppercase text-indigo-500 tracking-widest mb-4">What your runway protects</p>
        <div className="space-y-3">
          {EMERGENCIES.map((item, index) => (
            <div key={item.title} className="flex gap-3 items-center p-3 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={item.icon} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-black text-slate-900">{item.title}</p>
                <p className="text-[9px] font-medium text-slate-500">{item.detail}</p>
              </div>
              <span className="text-[9px] font-black text-indigo-400 flex-shrink-0">0{index + 1}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-indigo-50 rounded-2xl border border-indigo-100 p-3 sm:p-4">
          <p className="text-[9px] font-black text-indigo-500 uppercase">Minimum · 3 months</p>
          <p className="text-lg sm:text-xl font-black text-indigo-900">{fmt(base * 3)}</p>
        </div>
        <div className="bg-indigo-600 rounded-2xl p-3 sm:p-4 text-white">
          <p className="text-[9px] font-black text-indigo-100 uppercase">Target · 6 months</p>
          <p className="text-lg sm:text-xl font-black">{fmt(base * 6)}</p>
        </div>
      </div>
      <p className="text-[10px] font-medium text-slate-500">Based on {fmt(base)} of essential expenses each month.</p>
    </div>
  );
};